import React from 'react'
import styled from 'styled-components'
import { ListType } from 'types/graphql'
import { LocalStorageVariables, getLocalStorageItem, setLocalStorageItem } from 'utils/LocalStorage'
import { media } from 'utils/media'
import { Button } from 'lib/Button/Button'
import { FloatingPopover, useFloatingPopoverController } from 'lib/FloatingPopover'
import { LoadingCircle } from 'lib/LoadingCircle'
import { Text } from 'lib/Text'
import { useCurrentUser } from 'modules/auth/useCurrentUser'
import { useUserLists } from '../queries/userLists'
import { PromptHeader } from './PromptHeader'

const Box = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  padding: 24px;
  border-radius: 8px;
  cursor: auto;
  justify-content: center;
  background-color: white;
  max-width: 300px;
  ${media.phoneDown} {
    max-width: 100%;
    padding: 0;
  }
`

const PopoverButton = styled(Button)`
  align-self: stretch;
`

interface ContentProps {
  controls: ReturnType<typeof useFloatingPopoverController>
  username: string
}

const PopoverContent = ({ controls, username }: ContentProps) => {
  const { data: listsData } = useUserLists({ variables: { username }, skip: !username })

  const handleClick = () => {
    controls.setOpen(false)
    setLocalStorageItem(LocalStorageVariables.hasSeenWTTOnboarding, 'true')
  }

  if (!listsData?.user) {
    return (
      <Box>
        <LoadingCircle size={16} />
      </Box>
    )
  }

  const wttList = listsData?.user?.lists.find((list) => list.type === ListType.WantToTest)

  return (
    <Box>
      <PromptHeader listName="Brands I want to test" listPath={`/${username}/lists/${wttList?.id}`} />

      <Text size="ps" margin="1x">
        This is your personal list of brands you want to test in the future.
      </Text>

      <PopoverButton onClick={handleClick} size="small" label="Got it!" />
    </Box>
  )
}

interface Props {
  controls: ReturnType<typeof useFloatingPopoverController>
  skip?: boolean
  children: React.ReactElement
}

export const WantToTestPrompt = ({ skip, children, controls }: Props) => {
  const { user } = useCurrentUser()

  if (skip || !user) {
    return children
  }

  const hasUserSeenOnboarding = typeof window !== 'undefined' && getLocalStorageItem(LocalStorageVariables.hasSeenWTTOnboarding) === 'true'

  return (
    <FloatingPopover
      arrow
      placement="bottom"
      controls={controls}
      withHover={false}
      visible={!hasUserSeenOnboarding}
      content={!hasUserSeenOnboarding && <PopoverContent username={user.username} controls={controls} />}>
      {children}
    </FloatingPopover>
  )
}
